import { type ReactNode, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { cn } from '@/utils/cn';
import type { NavItem } from './sidebar-link';
import { SidebarLabel } from './sidebar-label';
import { ChildLink, Chevron } from './sidebar-child-link';
import { PARENT_CLS, PARENT_ON, PARENT_OFF } from './sidebar-styles';

type PB = {
  item: NavItem;
  active: boolean;
  open: boolean;
  collapsed: boolean;
  onToggle: () => void;
};

function ParentButton({
  item,
  active,
  open,
  collapsed,
  onToggle,
}: PB): ReactNode {
  return (
    <li>
      <button
        type="button"
        onClick={onToggle}
        title={item.label}
        className={cn(PARENT_CLS, 'w-full', active ? PARENT_ON : PARENT_OFF)}
      >
        <SidebarLabel item={item} collapsed={collapsed} />
        <Chevron open={open} collapsed={collapsed} />
      </button>
    </li>
  );
}

type EP = { item: NavItem; collapsed: boolean };

function useExpand(item: NavItem) {
  const active = useLocation().pathname.startsWith(item.to);
  const [open, setOpen] = useState(active);
  return {
    kids: item.children ?? [],
    active,
    open,
    toggle: () => setOpen(!open),
  };
}

export function ExpandableLink({ item, collapsed }: EP): ReactNode {
  const { kids, active, open, toggle } = useExpand(item);
  return (
    <>
      <ParentButton
        item={item}
        active={active}
        open={open}
        collapsed={collapsed}
        onToggle={toggle}
      />
      {open &&
        !collapsed &&
        kids.map((c) => (
          <ChildLink key={c.to} item={c} collapsed={collapsed} />
        ))}
    </>
  );
}
